/**
 * Ask-Mo chat widget loader.
 *
 * Injects the third-party widget script (ask-mo.mowilex.id) which
 * renders its own launcher button and chat panel into the page. We
 * only add the <script> tag -- everything visible is the widget's.
 *
 * Guardrails, same shape as services/analytics.ts:
 *   - Only loads when REACT_APP_CHAT_WIDGET_SRC and
 *     REACT_APP_CHAT_WIDGET_KEY are both set. Production's build
 *     (see .github/workflows/production-deploy.yml) sets neither, so
 *     the widget is staging-only by default.
 *   - Hard-blocked on REACT_APP_ENVIRONMENT === 'production' even if
 *     the vars leak into a prod build, because portal.mowilex.id
 *     serves a strict CSP (`script-src 'self'`) that would block the
 *     script anyway -- a console error nobody would notice. If the
 *     widget is ever wanted on production, add ask-mo.mowilex.id to
 *     script-src AND connect-src in
 *     deploy/production/nginx/portal.mowilex.id.conf first.
 *   - Injects once. StrictMode double-invokes effects in dev, and the
 *     component sits at app level, so the id check matters.
 */

import React from 'react';

const SCRIPT_ID = 'ask-mo-chat-widget';

const SRC = process.env.REACT_APP_CHAT_WIDGET_SRC || '';
const API = process.env.REACT_APP_CHAT_WIDGET_API || '';
const KEY = process.env.REACT_APP_CHAT_WIDGET_KEY || '';
const ENV = process.env.REACT_APP_ENVIRONMENT || '';

export function ChatWidget(): null {
  React.useEffect(() => {
    if (!SRC || !KEY) return;
    if (ENV === 'production') return;
    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SRC;
    script.async = true;
    script.setAttribute('data-api', API || SRC.replace(/\/widget\.js$/, ''));
    script.setAttribute('data-key', KEY);
    script.onerror = () => {
      // Non-fatal: the portal works fine without the widget. Log so a
      // failed load is visible in the console instead of silently
      // showing no launcher.
      console.warn(`Chat widget failed to load from ${SRC}`);
    };

    document.body.appendChild(script);
    // Deliberately no cleanup -- the widget builds its own DOM and
    // listeners on load, and this component is mounted for the life
    // of the app. Removing the tag wouldn't unload the widget anyway.
  }, []);

  return null;
}

export default ChatWidget;
